import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useRegion } from '../context/RegionContext';
import { auth } from '../firebaseConfig';
import './NabersEpdCategoriesPage.css';

function NabersEpdCategoriesPage() {
    const { selectedRegion } = useRegion();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [isFullPage, setIsFullPage] = useState(false);

    // Fetch data from backend
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const user = auth.currentUser;
                if (!user) {
                    throw new Error('Please log in to view this page');
                }
                
                const token = await user.getIdToken(true);
                
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/nabers-epd-categories`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-region': selectedRegion
                    }
                });
                
                console.log('✅ NABERS EPD Categories loaded:', response.data.length);
                setData(response.data);
            } catch (err) {
                console.error('❌ Error fetching NABERS EPD categories:', err);
                setError(err.response?.data?.error || err.message || 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        if (selectedRegion) {
            fetchData();
        }
    }, [selectedRegion]);

    // ✅ Toggle full page mode (hide sidebar and navbar)
    const toggleFullPage = () => {
        setIsFullPage(!isFullPage);
        
        if (!isFullPage) {
            // Hide sidebar and navbar
            const sidebar = document.querySelector('.sidebar, .Sidebar, [class*="sidebar"], nav[class*="side"]');
            const navbar = document.querySelector('.navbar, .Navbar, .top-bar, .TopBar, header, [class*="navbar"]');
            const mainContent = document.querySelector('.main-content, .MainContent, main, [class*="main-content"]');
            
            if (sidebar) {
                sidebar.style.display = 'none';
                sidebar.dataset.hiddenByFullpage = 'true';
            }
            if (navbar) {
                navbar.style.display = 'none';
                navbar.dataset.hiddenByFullpage = 'true';
            }
            if (mainContent) {
                mainContent.style.marginLeft = '0';
                mainContent.style.width = '100%';
            }
            
            // Add full page class to body
            document.body.classList.add('nabers-fullpage-mode');
        } else {
            // Restore sidebar and navbar
            const sidebar = document.querySelector('[data-hidden-by-fullpage="true"]');
            const navbar = document.querySelectorAll('[data-hidden-by-fullpage="true"]')[1];
            const mainContent = document.querySelector('.main-content, .MainContent, main, [class*="main-content"]');
            
            if (sidebar) {
                sidebar.style.display = '';
                delete sidebar.dataset.hiddenByFullpage;
            }
            if (navbar) {
                navbar.style.display = '';
                delete navbar.dataset.hiddenByFullpage;
            }
            if (mainContent) {
                mainContent.style.marginLeft = '';
                mainContent.style.width = '';
            }
            
            // Remove full page class from body
            document.body.classList.remove('nabers-fullpage-mode');
        }
    };

    // ✅ Cleanup on unmount - restore sidebar/navbar if user navigates away
    useEffect(() => {
        return () => {
            if (isFullPage) {
                const sidebar = document.querySelector('[data-hidden-by-fullpage="true"]');
                const navbar = document.querySelectorAll('[data-hidden-by-fullpage="true"]')[1];
                const mainContent = document.querySelector('.main-content, .MainContent, main, [class*="main-content"]');
                
                if (sidebar) {
                    sidebar.style.display = '';
                    delete sidebar.dataset.hiddenByFullpage;
                }
                if (navbar) {
                    navbar.style.display = '';
                    delete navbar.dataset.hiddenByFullpage;
                }
                if (mainContent) {
                    mainContent.style.marginLeft = '';
                    mainContent.style.width = '';
                }
                document.body.classList.remove('nabers-fullpage-mode');
            }
        };
    }, [isFullPage]);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        
        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(row => 
            Object.values(row).some(val => 
                val && val.toString().toLowerCase().includes(lowerSearch)
            )
        );
    }, [data, searchTerm]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        const sorted = [...filteredData].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = aVal.toString().toLowerCase();
            const bStr = bVal.toString().toLowerCase();
            
            if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [filteredData, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '⇅';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const formatColumnName = (key) => {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    const columns = useMemo(() => {
        if (data.length === 0) return [];
        return Object.keys(data[0]);
    }, [data]);

    if (loading) {
        return (
            <div className="nabers-epd-page">
                <div className="nabers-epd-loading">
                    <div className="spinner"></div>
                    <p>Loading NABERS EPD Categories...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="nabers-epd-page">
                <div className="nabers-epd-error">
                    <span className="error-icon">⚠️</span>
                    <h2>Error Loading Data</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`nabers-epd-page ${isFullPage ? 'full-page-mode' : ''}`}>
            <div className="nabers-epd-header">
                <div className="header-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h1>NABERS EPD Category Usage</h1>
                            <p className="subtitle">View environmental product declaration category usage statistics</p>
                        </div>
                        {/* ✅ Full Page Toggle Button */}
                        <button 
                            className="fullscreen-btn"
                            onClick={toggleFullPage}
                            title={isFullPage ? 'Exit Full Page View' : 'Enter Full Page View (Hide Sidebar)'}
                        >
                            {isFullPage ? (
                                <>
                                    <span className="fullscreen-icon">⬅</span>
                                    <span className="fullscreen-text">Exit Full Page</span>
                                </>
                            ) : (
                                <>
                                    <span className="fullscreen-icon">⛶</span>
                                    <span className="fullscreen-text">Full Page View</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
                
                <div className="header-controls">
                    <div className="search-bar">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="   Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button 
                                className="clear-search"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    <div className="stats-bar">
                        <div className="stat-item">
                            <span className="stat-label">Total Records:</span>
                            <span className="stat-value">{data.length}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Filtered:</span>
                            <span className="stat-value">{sortedData.length}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Region:</span>
                            <span className="stat-value">{selectedRegion}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="nabers-epd-table-container">
                <table className="nabers-epd-table">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th 
                                    key={column}
                                    onClick={() => handleSort(column)}
                                    className="sortable"
                                >
                                    <div className="th-content">
                                        <span>{formatColumnName(column)}</span>
                                        <span className="sort-indicator">
                                            {getSortIndicator(column)}
                                        </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="no-data">
                                    {searchTerm ? 'No results found matching your search' : 'No data available'}
                                </td>
                            </tr>
                        ) : (
                            sortedData.map((row, index) => (
                                <tr key={index}>
                                    {columns.map((column) => {
                                        const cellValue = row[column] != null ? row[column].toString() : '-';
                                        const isLongText = cellValue.length > 50;
                                        const truncatedValue = isLongText ? cellValue.substring(0, 50) + '...' : cellValue;
                                        
                                        return (
                                            <td 
                                                key={column}
                                                className={isLongText ? 'truncated-cell' : ''}
                                                title={isLongText ? cellValue : ''}
                                            >
                                                {truncatedValue}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default NabersEpdCategoriesPage;